// Moteur de calcul du simulateur de productivité — fonctions pures, testables.
// Convention de signes (source de vérité) :
//   heuresCibles       = CA / Gold                      → volume théorique correspondant à la cible
//   potentielOptim     = max(0, heures − heuresCibles)  → heures au-dessus du théorique (jamais "à supprimer")
//   besoinCapacite     = max(0, heuresCibles − heures)  → heures théoriquement manquantes pour absorber le CA à la cible

export const safeDiv = (a, b) => (b && isFinite(a / b) ? a / b : 0);

export function computeScenario({ ca, heures, gold }) {
  const productivite = safeDiv(ca, heures);
  const heuresCibles = safeDiv(ca, gold);
  const ecartGold = gold ? productivite - gold : 0;
  const ecartH = heures - heuresCibles;
  return {
    ca, heures, gold, productivite, heuresCibles, ecartGold,
    pctGold: safeDiv(productivite, gold),
    potentielOptim: Math.max(0, ecartH),
    besoinCapacite: Math.max(0, -ecartH),
    statut: !gold ? "na" : productivite >= gold ? "atteint" : productivite >= gold * 0.9 ? "proche" : "sous",
  };
}

export function compareScenarios(base, sim) {
  return {
    dCa: sim.ca - base.ca, dCaPct: safeDiv(sim.ca - base.ca, base.ca),
    dHeures: sim.heures - base.heures, dHeuresPct: safeDiv(sim.heures - base.heures, base.heures),
    dProd: sim.productivite - base.productivite, dEcartGold: sim.ecartGold - base.ecartGold,
  };
}

export const optimizedScenario = (base) => computeScenario({ ca: base.ca, heures: safeDiv(base.ca, base.gold), gold: base.gold });

export function buildMatrix({ ca, heures, gold, steps = 5 }) {
  const rows = [], cols = [];
  const caStep = Math.max(1000, Math.round(ca * 0.1 / 1000) * 1000);
  const hStep = Math.max(10, Math.round(heures * 0.1 / 10) * 10);
  for (let i = -2; i <= 2; i++) { rows.push(Math.max(0, ca + i * caStep)); cols.push(Math.max(0, heures + i * hStep)); }
  return { rows, cols, gold, cell: (r, c) => safeDiv(r, c) };
}

export const cellTone = (v, gold) => !gold ? "na" : v >= gold ? "vert" : v >= gold * 0.95 ? "vert_clair" : v >= gold * 0.85 ? "orange" : "rouge";

// Projection : répartit le CA/heures simulés sur les périodes réelles au prorata de leur poids observé.
export function projectPeriods(periods, simCa, simHeures, gold) {
  const totCa = periods.reduce((s, p) => s + (p.ca_ht || 0), 0), totH = periods.reduce((s, p) => s + (p.heure || 0), 0);
  return periods.map(p => {
    const ca = totCa ? simCa * (p.ca_ht || 0) / totCa : 0, heures = totH ? simHeures * (p.heure || 0) / totH : 0;
    const sc = computeScenario({ ca, heures, gold: p.gold_standard || gold });
    return { label: p.label || p.mois, ca, heures, productivite: sc.productivite, ecartGold: sc.ecartGold, statut: sc.statut, date_start: p.date_start, date_end: p.date_end };
  });
}

export const hiringHours = ({ nb = 1, hSemaine = 35, semaines = 4 }) => Math.max(0, nb) * Math.max(0, hSemaine) * Math.max(0, semaines);

export function narrative({ base, sim, cmp, gold, mode, fmtEur, fmtH, fmtEurH }) {
  const lines = [];
  lines.push(`Avec un CA prévisionnel de ${fmtEur(sim.ca)} et ${fmtH(sim.heures)}, la productivité simulée atteint ${fmtEurH(sim.productivite)} contre un Gold de ${fmtEurH(gold)}.`);
  lines.push(`Par rapport à la situation actuelle, ce scénario représente ${cmp.dCa >= 0 ? "+" : ""}${fmtEur(cmp.dCa)} de CA, ${cmp.dHeures >= 0 ? "+" : ""}${fmtH(cmp.dHeures)} et ${cmp.dProd >= 0 ? "+" : ""}${fmtEurH(cmp.dProd)} de productivité.`);
  if (mode === "gold") lines.push(`Pour atteindre votre Gold de ${fmtEurH(gold)} avec un CA de ${fmtEur(sim.ca)}, le volume théorique correspondant est de ${fmtH(sim.heuresCibles)}.`);
  const vigilance = [];
  if (sim.statut === "sous") vigilance.push(`La productivité simulée reste sous l'objectif (${fmtEurH(sim.ecartGold)}). Le potentiel d'optimisation est de ${fmtH(sim.potentielOptim)}.`);
  if (sim.besoinCapacite > 0) vigilance.push(`Le niveau d'heures est inférieur au volume théorique : besoin de capacité estimé à ${fmtH(sim.besoinCapacite)} pour absorber ce CA à la cible.`);
  if (cmp.dHeuresPct < -0.15) vigilance.push(`Réduction d'heures supérieure à 15 % : vérifiez la faisabilité opérationnelle (ouverture, production, hygiène).`);
  if (cmp.dCaPct > 0.15) vigilance.push(`Hausse de CA supérieure à 15 % : hypothèse ambitieuse à confronter à la tendance réelle du périmètre.`);
  return { lines, vigilance };
}
